// App version accessor. The value comes from `__VERSION__`, baked into the
// bundle at build time by vite.config.ts (commit count since a baseline →
// "0.8.N"). See docs/VERSION-NUMBERING.md.
import { useEffect, useState } from "react";

const formatVersion = (v: string): string =>
  !v || v === "dev" ? "Development" : `v${v}`;

const bakedRaw = (): string =>
  typeof __VERSION__ === "string" ? __VERSION__ : "dev";

/** Formatted, user-facing version string, e.g. "v0.8.1" or "Development". */
export const getVersion = (): string => formatVersion(bakedRaw());

/** Raw version string, e.g. "0.8.1" or "dev". */
export const getRawVersion = (): string => bakedRaw();

/**
 * Formatted version that stays fresh on page refresh during development.
 * The baked `__VERSION__` is frozen at dev-server start, so after committing
 * it goes stale; in dev this fetches the live count from the `/__version`
 * endpoint (see vite.config.ts) once on mount. In production it returns the
 * baked value with no network call.
 */
export const useVersion = (): string => {
  const [version, setVersion] = useState<string>(getVersion());

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    fetch("/__version")
      .then((r) => r.json())
      .then((d: { version?: string }) => {
        if (!cancelled && d?.version) setVersion(formatVersion(d.version));
      })
      .catch(() => {
        /* keep baked value */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return version;
};
